import Polygon from "@arcgis/core/geometry/Polygon";
import Point from "@arcgis/core/geometry/Point";
import {
  Patch,
  RevegetationArea,
  Tree,
  treeType,
  featureTypes,
} from "../lib/types";
import {
  getIntersectingPoints,
  getNextFeatureId,
  reorderIds,
} from "../utils/featureUtils";

export interface IState {
  patchesList: string[];
  treesList: string[];
  revegetationAreasList: string[];
  features: Record<string, Patch | Tree | RevegetationArea>;
}

export type PointsLookup = Record<string, Point>;

export const getTreesLookup = (state: IState) => {
  return state.treesList.reduce((acc: PointsLookup, x) => {
    acc[x] = new Point(state.features[x].geom);
    return acc;
  }, {});
};

const addFeature = (
  list: string[],
  mapping: Record<string, Patch | Tree | RevegetationArea>,
  feature: any,
  featureType: featureTypes
): [string[], Record<string, Patch | Tree | RevegetationArea>] => {
  const newFeature = { ...feature };
  let featureId = newFeature.id;
  if (!featureId) {
    featureId = getNextFeatureId(list, featureType);
    newFeature.id = featureId;
  }
  mapping[featureId] = newFeature;
  list.push(featureId);
  return [list, mapping];
};

const deleteFeature = (
  list: string[],
  mapping: Record<string, Patch | Tree | RevegetationArea>,
  featureId: string
): [string[], Record<string, Patch | Tree | RevegetationArea>] => {
  delete mapping[featureId];
  list = list.filter((x) => x !== featureId);
  return [list, mapping];
};

const convertTrees = (
  treeIds: string[],
  toType: treeType,
  mapping: Record<string, Patch | Tree | RevegetationArea>
) => {
  treeIds.forEach((x) => {
    const tree = mapping[x] as Tree;
    tree.treeType = toType;
  });
};

const reOrderListandMapping = (
  list: string[],
  mapping: Record<string, Patch | Tree | RevegetationArea>,
  featureType: featureTypes
): [string[], Record<string, Patch | Tree | RevegetationArea>] => {
  const reOrderedIds = reorderIds(list, featureType);
  const tempFeatures: Record<string, Patch | Tree | RevegetationArea> = {};
  list.forEach((x) => {
    tempFeatures[reOrderedIds[x]] = Object.assign(mapping[x], {
      id: reOrderedIds[x],
    });
    delete mapping[x];
  });
  mapping = Object.assign(mapping, tempFeatures);
  list = list.map((x) => reOrderedIds[x]);
  return [list, mapping];
};

function featuresReducer(state: IState, action: any) {
  switch (action.type) {
    case "addPatch": {
      const intersectingTrees = getIntersectingPoints(
        getTreesLookup(state),
        new Polygon(action.payload.geom)
      );
      [state.patchesList, state.features] = addFeature(
        state.patchesList,
        state.features,
        action.payload,
        featureTypes.patch
      );
      convertTrees(intersectingTrees, treeType.Patch, state.features);
      return state;
    }
    case "updatePatch": {
      const previousIntersectingTrees = getIntersectingPoints(
        getTreesLookup(state),
        new Polygon(state.features[action.payload.id].geom)
      );
      const newIntersectingTrees = getIntersectingPoints(
        getTreesLookup(state),
        new Polygon(action.payload.geom)
      );
      state.features[action.payload.id] = action.payload;
      convertTrees(
        previousIntersectingTrees,
        treeType.Scattered,
        state.features
      );
      convertTrees(newIntersectingTrees, treeType.Patch, state.features);
      return state;
    }
    case "deletePatch": {
      const deleteId = action.payload.id;
      const intersectingTrees = getIntersectingPoints(
        getTreesLookup(state),
        new Polygon(state.features[deleteId].geom)
      );
      [state.patchesList, state.features] = deleteFeature(
        state.patchesList,
        state.features,
        deleteId
      );
      [state.patchesList, state.features] = reOrderListandMapping(
        state.patchesList,
        state.features,
        featureTypes.patch
      );
      convertTrees(intersectingTrees, treeType.Scattered, state.features);
      return state;
    }
    case "addTree": {
      [state.treesList, state.features] = addFeature(
        state.treesList,
        state.features,
        action.payload,
        featureTypes.tree
      );
      return state;
    }
    case "updateTree": {
      state.features[action.payload.id] = action.payload;
      return state;
    }
    case "deleteTrees": {
      for (let x of action.payload) {
        [state.treesList, state.features] = deleteFeature(
          state.treesList,
          state.features,
          x
        );
      }
      [state.treesList, state.features] = reOrderListandMapping(
        state.treesList,
        state.features,
        featureTypes.tree
      );
      return state;
    }
    case "mergePatches": {
      const { polygonIds, mergedFeature, removedPolygonGeometry } =
        action.payload;
      const previousIntersectingTrees = removedPolygonGeometry
        ? getIntersectingPoints(getTreesLookup(state), removedPolygonGeometry)
        : undefined;
      const newIntersectingTrees = getIntersectingPoints(
        getTreesLookup(state),
        new Polygon(mergedFeature.geom)
      );
      [state.patchesList, state.features] = addFeature(
        state.patchesList,
        state.features,
        mergedFeature,
        featureTypes.patch
      );
      polygonIds.forEach((x: string) => {
        [state.patchesList, state.features] = deleteFeature(
          state.patchesList,
          state.features,
          x
        );
      });
      [state.patchesList, state.features] = reOrderListandMapping(
        state.patchesList,
        state.features,
        featureTypes.patch
      );
      if (previousIntersectingTrees) {
        convertTrees(
          previousIntersectingTrees,
          treeType.Scattered,
          state.features
        );
      }
      convertTrees(newIntersectingTrees, treeType.Patch, state.features);
      return state;
    }
    case "addRevegetationArea": {
      [state.revegetationAreasList, state.features] = addFeature(
        state.revegetationAreasList,
        state.features,
        action.payload,
        featureTypes.revegetationArea
      );
      return state;
    }
    case "updateRevegetationArea": {
      state.features[action.payload.id] = action.payload;
      return state;
    }
    case "deleteRevegetationArea": {
      const deleteId = action.payload.id;
      [state.revegetationAreasList, state.features] = deleteFeature(
        state.revegetationAreasList,
        state.features,
        deleteId
      );
      [state.revegetationAreasList, state.features] = reOrderListandMapping(
        state.revegetationAreasList,
        state.features,
        featureTypes.revegetationArea
      );
      return state;
    }
    case "mergeRevegetationAreas": {
      const { polygonIds, mergedFeature } = action.payload;
      [state.revegetationAreasList, state.features] = addFeature(
        state.revegetationAreasList,
        state.features,
        mergedFeature,
        featureTypes.revegetationArea
      );
      polygonIds.forEach((x: string) => {
        [state.revegetationAreasList, state.features] = deleteFeature(
          state.revegetationAreasList,
          state.features,
          x
        );
      });
      [state.revegetationAreasList, state.features] = reOrderListandMapping(
        state.revegetationAreasList,
        state.features,
        featureTypes.revegetationArea
      );

      return state;
    }
    case "clear": {
      return initialState;
    }
    default: {
      throw Error("Unknown action: " + action.type);
    }
  }
}

export const initialState: IState = {
  patchesList: [],
  treesList: [],
  revegetationAreasList: [],
  features: {},
};

export default featuresReducer;
